<template>
  <!-- Bag Modal -->
  <div class="bag-modal" @click="closeModal">
    <div class="bag-card" @click.stop>
      <!-- Fixed Header -->
      <div class="bag-header">
        <h3>背包物品</h3>
      </div>

      <!-- Category Tabs -->
      <div class="bag-tabs">
        <!-- 全部分类始终显示 -->
        <button
          @click="activeTab = '全部'"
          :class="['tab-btn', { active: currentActiveTab === '全部' }]"
        >
          全部 ({{ Object.keys(categorizedItems['全部'] || {}).length }})
        </button>

        <!-- 其他分类按顺序显示 -->
        <button
          v-for="category in categoryOrder"
          :key="category"
          @click="activeTab = category"
          :class="['tab-btn', { active: currentActiveTab === category }]"
          :disabled="!categorizedItems[category] || Object.keys(categorizedItems[category]).length === 0"
        >
          {{ category }} ({{ categorizedItems[category] ? Object.keys(categorizedItems[category]).length : 0 }})
        </button>
      </div>

      <!-- Scrollable Content -->
      <div class="bag-content">
        <div v-if="Object.keys(bag).length === 0" class="empty-bag">
          <p>背包为空</p>
          <p class="empty-tip">通过探索和修炼来获取物品</p>
        </div>

        <div v-else-if="Object.keys(currentCategoryItems).length === 0" class="empty-category">
          <p>{{ currentActiveTab }}分类为空</p>
          <p class="empty-tip">该分类下暂无物品</p>
        </div>

        <div v-else class="bag-items">
          <div
            v-for="(count, itemName) in currentCategoryItems"
            :key="itemName"
            class="bag-item"
          >
            <div class="item-info">
              <span class="item-name">{{ itemName }}</span>
              <span v-if="count > 0" class="item-count">×{{ count }}</span>
            </div>
            <div class="item-actions">
              <button
                @click.stop="showItemInfo(itemName)"
                class="view-btn"
              >
                查看
              </button>
              <button
                @click.stop="useItem(itemName)"
                class="use-btn"
                :disabled="!canUseItem(itemName)"
              >
                使用
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="bag-footer">
        <button @click="closeModal" class="close-btn">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import UtilsManager from '../utils/UtilsManager.js';

export default {
  name: 'BagModal',
  props: {
    gameState: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: null
    };
  },
  computed: {
    bag() {
      return this.gameState.bag || {};
    },
    categorizedItems() {
      return UtilsManager.getCategorizedItems(this.bag);
    },
    categoryOrder() {
      return UtilsManager.getCategoryDisplayOrder();
    },
    defaultTab() {
      // Prefer "全部" category as default since it's most useful
      if (Object.keys(this.categorizedItems['全部'] || {}).length > 0) {
        return '全部';
      }

      // Otherwise find first category with items
      for (const [category, items] of Object.entries(this.categorizedItems)) {
        if (category !== '全部' && Object.keys(items).length > 0) {
          return category;
        }
      }

      return '全部'; // fallback to "全部" even if empty
    },
    currentActiveTab() {
      return this.activeTab || this.defaultTab;
    },
    currentCategoryItems() {
      return this.categorizedItems[this.currentActiveTab] || {};
    }
  },
  mounted() {
    // Set default active tab when component mounts
    if (!this.activeTab) {
      this.activeTab = this.defaultTab;
    }
  },
  methods: {
    closeModal() {
      this.$emit('close-modal');
    },

    showItemInfo(itemName) {
      const info = UtilsManager.getItemInfo(itemName);
      // Emit event to parent to show item info modal
      this.$emit('show-item-info', info);
    },

    canUseItem(itemName) {
      // Check if item can be used (has use logic in pills or treasures or manuals)
      const itemLower = itemName.toLowerCase();

      // Check if it's a manual/book for unlocking recipes
      if (itemName.endsWith('秘籍') || itemName.endsWith('丹方')) {
        return true;
      }

      const pills = ['灵草', '回春丹', '聚气丹', '筑基丹', '金丹', '元婴丹', '化神丹', '渡劫丹', '仙丹'];
      const treasures = ['飞剑', '法宝', '灵器', '宝器', '仙器'];

      return pills.some(p => p.toLowerCase() === itemLower) ||
             treasures.some(t => t.toLowerCase() === itemLower);
    },

    useItem(itemName) {
      // Emit event to parent to use the item
      this.$emit('use-item', itemName);
      // Close modal after using item
      this.closeModal();
    }
  }
};
</script>

<style scoped>
.bag-modal {
  position: absolute;
  inset: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.8);
  z-index: 250;
  backdrop-filter: blur(2px);
}

.bag-card {
  background: #071328;
  border-radius: 12px;
  border: 1px solid #334155;
  color: #e6eef8;
  min-width: 400px;
  max-width: 600px;
  height: 500px;
  display: flex;
  flex-direction: column;
}

.bag-header {
  flex-shrink: 0;
  padding: 12px 20px 8px 20px;
  border-bottom: 1px solid #334155;
}

.bag-tabs {
  flex-shrink: 0;
  padding: 8px;
  border-bottom: 1px solid #334155;
  display: flex;
  gap: 4px;
  overflow-x: auto;
}

.tab-btn {
  padding: 4px 8px;
  border: 1px solid #475569;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  transition: all 0.2s;
  min-width: 60px;
}

.tab-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  border-color: #64748b;
}

.tab-btn.active {
  background: #60a5fa;
  color: white;
  border-color: #60a5fa;
}

.tab-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.bag-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.bag-footer {
  flex-shrink: 0;
  padding: 12px 20px 16px 20px;
  border-top: 1px solid #334155;
  display: flex;
  justify-content: center;
}

.bag-card h3 {
  margin: 0;
  font-size: 18px;
  color: #60a5fa;
  text-align: center;
}

.bag-card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  text-align: center;
  margin-top: 8px;
}

.empty-bag {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
}

.empty-tip {
  margin-top: 8px;
  font-size: 12px;
}

.empty-category {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
}

.bag-items {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.bag-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  min-height: 20px;
}

.bag-item:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.2);
}

.item-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-name {
  color: #dbeafe;
  font-weight: 500;
  font-size: 12px;
}

.item-count {
  color: #60a5fa;
  font-size: 11px;
  background: rgba(96, 165, 250, 0.1);
  padding: 1px 4px;
  border-radius: 3px;
}

.item-actions {
  display: flex;
  gap: 8px;
}

.view-btn {
  padding: 4px 8px;
  border: 1px solid #60a5fa;
  background: transparent;
  color: #60a5fa;
  cursor: pointer;
  border-radius: 3px;
  font-size: 11px;
  transition: all 0.2s;
}

.view-btn:hover {
  background: #60a5fa;
  color: white;
}

.use-btn {
  padding: 4px 8px;
  border: 1px solid #10b981;
  background: transparent;
  color: #10b981;
  cursor: pointer;
  border-radius: 3px;
  font-size: 11px;
  transition: all 0.2s;
}

.use-btn:hover:not(:disabled) {
  background: #10b981;
  color: white;
}

.use-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #64748b;
  color: #64748b;
}

.close-btn {
  padding: 8px 16px;
  border: 1px solid #dc2626;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #dc2626;
  color: white;
}

/* Mobile responsive */
@media (max-width: 600px) {
  .bag-card {
    min-width: 90vw;
    max-width: 90vw;
    height: 70vh;
  }
}
</style>
